import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { useFirestore, useAuth } from '../hooks/useFirebase';
import { contractProgressType } from '../interfaces/contractProgressTypes';

export const name = 'readContractProgressCollection';
export const description = 'Reads contract progress entries directly from Firestore, optionally filtered by contractor, customer, project_number, and created_at range.';
export const parameters = {
  contractor: 'Optional contractor name to filter progress entries by.',
  customer: 'Optional customer name to filter progress entries by.',
  project_number: 'Optional project number (e.g. "M-22-04") to filter progress entries by.',
  created_at_after: 'Optional start date filter (format: YYYY-MM-DD) to query progress entries created on or after this date.',
  created_at_before: 'Optional end date filter (format: YYYY-MM-DD) to query progress entries created on or before this date.'
};
export const outputTextForUI = 'Szerződéses haladások lekérése';
export const useOnMobile = true;

export async function readContractProgressCollection(args?: {
  contractor?: string;
  customer?: string;
  project_number?: string;
  created_at_after?: string;
  created_at_before?: string;
}) {
  const db = useFirestore();
  const user = useAuth();

  const filterProjectNumber = args?.project_number;
  const filterContractor = args?.contractor;
  const filterCustomer = args?.customer;
  const filterCreatedAtAfter = args?.created_at_after;
  const filterCreatedAtBefore = args?.created_at_before;

  const constraints: any[] = [where('user_id', '==', user.uid)];

  if (filterProjectNumber) {
    constraints.push(where('project_number', '==', filterProjectNumber));
  }
  if (filterContractor) {
    constraints.push(where('contractor', '==', filterContractor));
  }
  if (filterCustomer) {
    constraints.push(where('customer', '==', filterCustomer));
  }
  if (filterCreatedAtAfter) {
    const startTime = new Date(filterCreatedAtAfter);
    if (!isNaN(startTime.getTime())) {
      constraints.push(where('created_at', '>=', Timestamp.fromDate(startTime)));
    }
  }
  if (filterCreatedAtBefore) {
    const endTime = new Date(new Date(filterCreatedAtBefore).getTime() + (24 * 60 * 60 * 1000 - 1));
    if (!isNaN(endTime.getTime())) {
      constraints.push(where('created_at', '<=', Timestamp.fromDate(endTime)));
    }
  }

  const q = query(
    collection(db, 'contract-progress'),
    ...constraints
  );

  const snapshot = await getDocs(q);
  const progressList: contractProgressType[] = [];

  snapshot.forEach((doc) => {
    progressList.push({
      id: doc.id,
      ...doc.data()
    } as unknown as contractProgressType);
  });

  // Sort by created_at or last_modified descending
  progressList.sort((a, b) => {
    const timeA = a.created_at?.seconds || a.last_modified?.seconds || 0;
    const timeB = b.created_at?.seconds || b.last_modified?.seconds || 0;
    return timeB - timeA;
  });

  // Limit to 50 entries
  const limitedProgress = progressList.slice(0, 50);

  return {
    success: true,
    count: limitedProgress.length,
    progress: limitedProgress
  };
}
